import React, { useEffect, useRef, useState } from 'react';
import './home.css';
import Layout from '../components/layout';
import Pills from '../components/pills';
import arrow from '../assets/arrow.svg';
import Button from '../components/button';
import bluearrow from '../assets/bluearrow.svg';
import aiimg from '../assets/aiimg.png';
import startupimg from '../assets/startupimg.png';
import futureimg from '../assets/futureimg.png';
import creativecodingimg from '../assets/creativecodingimg.png';
import xrimg from '../assets/xrimg.png';
import SphereCarousel from '../components/spherecarousel';
import neuralinkimg from '../assets/neuralink.png';
import agriwaverimg from '../assets/agriwave.png';
import medisyncimg from '../assets/medisync.png';
import flowpayimg from '../assets/flowpay.png';
import { useGsapAnimations } from '../hooks/useGsapAnimations';
import { supabase } from '../supabase';

const emptyCountdown = {
  display: '-- : -- : --',
  parts: ['--', '--', '--'],
  label: 'Loading event countdown',
};

const padTime = (value) => String(Math.max(0, value)).padStart(2, '0');

const getCountdown = (eventStartDate) => {
  if (!eventStartDate) return emptyCountdown;

  const targetTime = new Date(eventStartDate).getTime();

  if (Number.isNaN(targetTime)) {
    return {
      display: '-- : -- : --',
      parts: ['--', '--', '--'],
      label: 'Event date unavailable',
    };
  }

  const remainingSeconds = Math.floor(Math.max(0, targetTime - Date.now()) / 1000);
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;

  return {
    display: `${padTime(hours)} : ${padTime(minutes)} : ${padTime(seconds)}`,
    parts: [padTime(hours), padTime(minutes), padTime(seconds)],
    label: `${hours} hours, ${minutes} minutes, ${seconds} seconds until the event`,
  };
};

const Home = () => {
  const pageRef = useRef(null);
  const [eventStartDate, setEventStartDate] = useState(null);
  const [countdown, setCountdown] = useState(emptyCountdown);

  useGsapAnimations(pageRef);

  useEffect(() => {
    let isMounted = true;

    const loadEventSettings = async () => {
      const { data, error } = await supabase
        .from('event_settings')
        .select('event_start_date')
        .order('event_start_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!isMounted || error) return;

      setEventStartDate(data?.event_start_date || null);
    };

    loadEventSettings();

    const channel = supabase
      .channel('home-event-settings')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'event_settings' },
        (payload) => {
          const nextStartDate = payload.new?.event_start_date;

          if (nextStartDate) {
            setEventStartDate(nextStartDate);
          } else {
            loadEventSettings();
          }
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    setCountdown(getCountdown(eventStartDate));

    const intervalId = window.setInterval(() => {
      setCountdown(getCountdown(eventStartDate));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [eventStartDate]);

  const cards = [
  {
    number: '01',
    title: 'AI Motion Lab',
    description: 'Interactive Installation',
    image: aiimg,
    status: 'LIVE NOW',
    background: 'linear-gradient(180deg, #0B1627 0%, #233552 100%)',
  },
  {
    number: '02',
    title: 'Startup Arena',
    description: "Meet tomorrow's game changers",
    image: startupimg,
    status: 'LIVE NOW',
    background: 'linear-gradient(118deg, #171B21 34.05%, #48556B 94.14%)',
  },
  {
    number: '03',
    title: 'Future Mobility',
    description: 'Redefining movement. Reimagining cities.',
    image: futureimg,
    status: 'STARTS IN 25 MIN',
    background: 'linear-gradient(188deg, #CCDDED 9.4%, #A3BCD5 47.69%, #5D81A7 93.53%)',
  },
  {
    number: '04',
    title: 'Creative Coding Lab',
    description: 'Code. Create. Compile the future.',
    image: creativecodingimg,
    status: 'STARTS IN 40 MIN',
    background: 'linear-gradient(161deg, #1A1834 10.26%, #232243 53.97%, #745E81 97.68%)',
  },
  {
    number: '05',
    title: 'XR Space',
    description: 'Immersive reality. Expanded possibilities.',
    image: xrimg,
    status: 'LIVE NOW',
    background: 'linear-gradient(161deg, #5F62B7 10.26%, #404386 29.8%, #212356 53.55%, #2B3256 97.68%)',
  },
];

const startups = [
  { name: 
    'NeuralLink AI', 
    tag: 'AI Innovation', 
    img: neuralinkimg 
  },
  { name: 'AgriWave', 
    tag: 'Climate Tech', 
    img: agriwaverimg 
  },
  { name: 'MediSync', 
    tag: 'HealthTech', 
    img: medisyncimg 
  },
  { name: 'FlowPay', 
    tag: 'FinTech', 
    img: flowpayimg 
  },
];

  return ( <Layout>
    <div className='home_page' ref={pageRef}>
      <section className='home_title' data-gsap-intro>
        <p>WELCOME BACK ZEINA!</p>
        <h1>Explore <br /> <span>TECHNE</span></h1>
      </section>

      <section className='pills' data-gsap-stagger="children" data-gsap-start="top 96%">
        <Pills pillstyle='pillstyle1' pillinside='We' />
        <Pills pillstyle='pillstyle2' pillinside='Create' />
        <Pills pillstyle='pillstyle3' pillinside={<img src={arrow} alt="Arrow" />} />
        <Pills pillstyle='pillstyle4' pillinside={<span className="gradient_text">Future</span>} />
      </section>

      <div className="section_label" data-gsap-reveal data-gsap-direction="left">
            <span className="label_line" />
            <p>YOUR TICKET</p>
          </div>

      <section className='home_ticket' data-gsap-reveal data-gsap-scale="0.92">
        <div className='mticket_title'>
          <h2>View Ticket</h2>
          <Button className='btnstyle4' img={bluearrow} aria-label="Open ticket" />
        </div>
        <div className='mticket_bottom'>
          <div className='countdown'>
            <div className='countdown_numbers' aria-label={countdown.label} aria-live="polite" role="timer">
              {countdown.parts.map((part, index) => (
                <React.Fragment key={index}>
                  <span className={`countdown_segment ${part.length > 2 ? 'countdown_segment_long' : ''}`}>
                    {part}
                  </span>
                  {index < countdown.parts.length - 1 && <span className='countdown_separator'>:</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

    <section className="techne_experiences">
      <div className="section_label" data-gsap-reveal data-gsap-direction="left">
        <span className="label_line" />
        <p>TECHNE EXPERIENCES</p>
      </div>
      <SphereCarousel cards={cards} />
    </section>

    <div className="section_label" data-gsap-reveal data-gsap-direction="left">
            <span className="label_line" />
            <p>STARTUP SPOTLIGHT</p>
          </div>

     <section className="spotlight_wrapper" data-gsap-reveal data-gsap-scale="0.94">
 

      <div className="spotlight_front">
        <svg
          className="spotlight_shape_svg"
          viewBox="0 0 216 205"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <path
            d="M0 25C0 11.1929 11.1929 0 25 0H86.5527C95.4916 0 103.75 4.77263 108.213 12.5175L110.787 16.9825C115.25 24.7274 123.508 29.5 132.447 29.5H191C204.807 29.5 216 40.6929 216 54.5V180C216 193.807 204.807 205 191 205H25C11.1929 205 0 193.807 0 180V25Z"
            fill="var(--blue2, #7ca4c3)"
          />
        </svg>
 
        <div className="spotlight_content">
          <div className="spotlight_header" data-gsap-reveal data-gsap-direction="down">
            <h2 className="spotlight_series">Startup Spotlight</h2>
            <div className="spotlight_dots"><span /><span /></div>
          </div>
 
          <p className="spotlight_title" data-gsap-reveal data-gsap-direction="left">Meet the innovators shaping tomorrow</p>
 
          <div className="spotlight_scroll" data-gsap-stagger=".spotlight_card" data-gsap-start="top 92%">
            {startups.map((s, i) => (
              <div className="spotlight_card" key={i} style={{ backgroundImage: `url(${s.img})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="spotlight_card_overlay">
                  <span className="spotlight_card_tag">{s.tag}</span>
                  <p className="spotlight_card_name">{s.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
 
    </section>
      
    </div>
  </Layout> );
}
 
export default Home;
